import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle2, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ConfirmBeneficiary() {
  const { token } = useParams();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/api/beneficiaries/confirm/${token}`)
      .then(r => {
        setPreview(r.data);
        if (r.data.already_confirmed) setConfirmed(true);
      })
      .catch(e => setError(e.response?.data?.detail || "Link invalid or expired."));
  }, [token]);

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      await axios.post(`${API_BASE}/api/beneficiaries/confirm/${token}`);
      setConfirmed(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="confirm-beneficiary-page">
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 text-forest mb-6" data-testid="confirm-brand">
          <Shield className="w-5 h-5" strokeWidth={1.5} />
          <span className="font-serif text-xl">Withdraw Guard</span>
        </Link>

        <div className="bg-linen border border-sand rounded-3xl p-8 sm:p-10">
          {error && (
            <div className="text-center">
              <AlertTriangle className="w-10 h-10 text-clay mx-auto" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl mt-4">Something&apos;s off</h2>
              <p className="text-sm text-moss mt-2" data-testid="confirm-error">{error}</p>
            </div>
          )}

          {!error && !preview && (
            <p className="text-center text-sm text-sage">Loading…</p>
          )}

          {!error && preview && confirmed && (
            <div className="text-center" data-testid="confirm-success">
              <CheckCircle2 className="w-12 h-12 text-forest mx-auto" strokeWidth={1.5} />
              <h2 className="font-serif text-3xl mt-5">Confirmed</h2>
              <p className="text-sm text-moss mt-3 leading-relaxed">
                Thanks, {preview.beneficiary_name}. {preview.user_name} has been notified that you accepted being their trusted contact.
              </p>
            </div>
          )}

          {!error && preview && !confirmed && (
            <div data-testid="confirm-prompt">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Trusted contact request</div>
              <h2 className="font-serif text-3xl mt-2">Hi {preview.beneficiary_name}</h2>
              <p className="text-sm text-moss mt-4 leading-relaxed">
                <strong>{preview.user_name}</strong> has listed you as a trusted contact on their Withdraw Guard account.
                If something ever prevented them from accessing their funds, you would be contacted.
              </p>
              <p className="text-sm text-moss mt-3">
                Nothing changes for you today — confirming just acknowledges the listing.
              </p>
              <button onClick={confirm} disabled={busy}
                data-testid="confirm-btn"
                className="mt-6 w-full bg-forest text-white rounded-xl py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-60">
                {busy ? "Confirming…" : `Confirm I'm ${preview.beneficiary_name}`}
              </button>
              <p className="text-[11px] text-sage mt-4 leading-relaxed">
                If you don&apos;t recognise {preview.user_name}, you can safely close this page — they&apos;ll see you didn&apos;t confirm.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
